import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, TrendingUp, TrendingDown, RefreshCw, Wallet, LineChart as LineChartIcon, FileDown } from 'lucide-react';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceArea,
} from 'recharts';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { salesAPI, sitesAPI } from '@/lib/api';
import { holidayAreas } from '@/lib/martiniqueCalendar';
import { formatCurrency } from '@/lib/utils';
import { useAuth } from '@/context/AuthContext';
import SalesNotePopover, { getCategoryStyle } from '../../components/sales/SalesNotePopover';
import ForecastSection from './ForecastSection';
import StaffingWeek from './StaffingWeek';

const PERIODS = [
    { id: 'day', label: 'Quotidien', subtitle: 'vs J-1' },
    { id: 'week', label: 'Hebdomadaire', subtitle: 'vs semaine N-1' },
    { id: 'month', label: 'Mensuel', subtitle: 'vs mois N-1' },
];

const SITE_COLORS = {
    'FE Génipa':  '#10b981', // emerald
    'FE Cluny':   '#3b82f6', // blue
    'FE Dillon':  '#f59e0b', // amber
    'FE Océanis': '#8b5cf6', // violet
    'FE Vauclin': '#ec4899', // pink
    'Total':      '#0f172a', // slate-900
};

const MONTHS_FR = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];
const addDaysIso = (iso, n) => { const d = new Date(iso + 'T12:00:00'); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };

export default function FinancialTab() {
    const { user, hasPermission } = useAuth();
    // Site restriction: users with allowed_sites only see their own site(s)
    const allowedIds = user?.allowed_sites?.length ? user.allowed_sites : null;
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [chartData, setChartData] = useState(null);
    const [chartLoading, setChartLoading] = useState(true);
    const [visibleSites, setVisibleSites] = useState({
        'FE Génipa': true, 'FE Cluny': true, 'FE Dillon': true, 'FE Océanis': true, 'FE Vauclin': true, 'Total': false,
    });
    const [chartMode, setChartMode] = useState('daily'); // 'daily' | 'cumul'
    const [scope, setScope] = useState('week'); // 'month' | 'week' | 'year' — Semaine par défaut
    const [showPrevYear, setShowPrevYear] = useState(false); // N-1 overlay (year & month scopes)
    const [prevYearData, setPrevYearData] = useState(null);
    const [anchorDate, setAnchorDate] = useState(todayIso());

    // Compute the date range from scope + anchorDate
    const { startDate, endDate, label } = useMemo(() => {
        if (scope === 'year') {
            const y = parseInt(anchorDate.slice(0, 4), 10);
            return {
                startDate: `${y}-01-01`,
                endDate: `${y}-12-31`,
                label: `Année ${y}`,
            };
        }
        if (scope === 'month') {
            const [y, m] = anchorDate.split('-').map(Number);
            const first = `${y}-${String(m).padStart(2, '0')}-01`;
            const lastDay = new Date(y, m, 0).getDate();
            const last = `${y}-${String(m).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
            return { startDate: first, endDate: last, label: `${MONTHS_FR[m - 1]} ${y}` };
        }
        // week (Monday → Sunday containing anchorDate)
        const d = new Date(anchorDate + 'T00:00:00');
        const dow = d.getDay();
        const diff = dow === 0 ? -6 : 1 - dow;
        const mon = new Date(d); mon.setDate(d.getDate() + diff);
        const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
        const isoWeek = isoWeekNumber(mon);
        return {
            startDate: toIso(mon),
            endDate: toIso(sun),
            label: `S${isoWeek} (${toIso(mon).slice(8, 10)}/${toIso(mon).slice(5, 7)} → ${toIso(sun).slice(8, 10)}/${toIso(sun).slice(5, 7)})`,
        };
    }, [scope, anchorDate]);

    const reload = async () => {
        setLoading(true);
        try {
            const r = await salesAPI.financialSummary();
            setData(r.data);
            // On first load, anchor the (week/month/year) view on the most
            // recent date that actually has sales — avoids landing on an
            // empty current week (weekend / out-of-season).
            if (r.data?.latest_sales_date) {
                setAnchorDate(prev => prev === todayIso() ? r.data.latest_sales_date : prev);
            }
        } finally {
            setLoading(false);
        }
    };
    const [notes, setNotes] = useState([]);
    const visibleNotes = useMemo(
        () => allowedIds ? notes.filter(n => !n.site_id || allowedIds.includes(n.site_id)) : notes,
        [notes, allowedIds]
    );
    const [sitesList, setSitesList] = useState([]);
    // In "Année" view, collapse the notes table by default (often very long).
    // User can expand it on demand. Other views keep it always visible.
    const [notesPanelOpen, setNotesPanelOpen] = useState(false);
    const reloadChart = async () => {
        setChartLoading(true);
        try {
            const [r, n, s] = await Promise.all([
                salesAPI.dailyCa({ start_date: startDate, end_date: endDate }),
                salesAPI.listNotes({ start_date: startDate, end_date: endDate }),
                sitesList.length ? Promise.resolve({ data: sitesList }) : sitesAPI.getAll(),
            ]);
            setChartData(r.data);
            setNotes(n.data || []);
            if (!sitesList.length) setSitesList(s.data || []);
        } finally {
            setChartLoading(false);
        }
    };
    const reloadNotesOnly = async () => {
        try {
            const n = await salesAPI.listNotes({ start_date: startDate, end_date: endDate });
            setNotes(n.data || []);
        } catch (e) { /* silent */ }
    };

    // Map of date -> list of notes (for fast lookup in tooltip / dot)
    const notesByDate = useMemo(() => {
        const m = {};
        for (const n of visibleNotes) {
            if (!m[n.date]) m[n.date] = [];
            m[n.date].push(n);
        }
        return m;
    }, [visibleNotes]);
    const feSites = useMemo(
        () => sitesList.filter(s => SITE_COLORS[s.name] && (!allowedIds || allowedIds.includes(s.id))),
        [sitesList, allowedIds]
    );
    const allowedNames = useMemo(() => {
        if (!allowedIds) return null;
        return new Set(sitesList.filter(s => allowedIds.includes(s.id)).map(s => s.name));
    }, [allowedIds, sitesList]);

    useEffect(() => { reload(); }, []);
    useEffect(() => { reloadChart(); /* eslint-disable-next-line */ }, [startDate, endDate]);

    // Load N-1 series only when the user enables the overlay in year OR month scope.
    // For year: whole previous year. For month: same month one year earlier.
    useEffect(() => {
        if (!showPrevYear || (scope !== 'year' && scope !== 'month')) {
            setPrevYearData(null);
            return;
        }
        if (scope === 'year') {
            const y = parseInt(anchorDate.slice(0, 4), 10) - 1;
            salesAPI.dailyCa({ start_date: `${y}-01-01`, end_date: `${y}-12-31` })
                .then(r => setPrevYearData(r.data))
                .catch(() => setPrevYearData(null));
        } else {
            // Mois : alignement des jours de semaine — on compare à J-364 (52 semaines
            // exactement), chaque jour N-1 tombe ainsi le même jour de semaine.
            const [y, m] = anchorDate.split('-').map(Number);
            const first = `${anchorDate.slice(0, 7)}-01`;
            const lastDay = new Date(y, m, 0).getDate();
            const last = `${anchorDate.slice(0, 7)}-${String(lastDay).padStart(2, '0')}`;
            salesAPI.dailyCa({ start_date: addDaysIso(first, -364), end_date: addDaysIso(last, -364) })
                .then(r => setPrevYearData(r.data))
                .catch(() => setPrevYearData(null));
        }
    }, [scope, showPrevYear, anchorDate]);

    // Shortcuts list (Jan 2024 → today)
    const monthShortcuts = useMemo(() => buildMonthShortcuts(), []);
    const weekShortcuts = useMemo(() => buildWeekShortcuts(), []);
    const yearShortcuts = useMemo(() => buildYearShortcuts(), []);
    const activeKey = scope === 'month'
        ? anchorDate.slice(0, 7)
        : scope === 'year'
            ? anchorDate.slice(0, 4)
            : startDate;
    const weekScrollerRef = React.useRef(null);
    const monthScrollerRef = React.useRef(null);
    const yearScrollerRef = React.useRef(null);
    useEffect(() => {
        let ref;
        if (scope === 'week') ref = weekScrollerRef;
        else if (scope === 'year') ref = yearScrollerRef;
        else ref = monthScrollerRef;
        if (!ref.current) return;
        let sel;
        if (scope === 'week') sel = `[data-week-key="${activeKey}"]`;
        else if (scope === 'year') sel = `[data-year-key="${activeKey}"]`;
        else sel = `[data-month-key="${activeKey}"]`;
        // Center the active chip horizontally inside the scroller using
        // bounding rects — avoids scrollIntoView's side effect of scrolling
        // ancestor axes and works regardless of offsetParent placement.
        // We schedule via rAF so the layout is up-to-date on tab activation
        // and after the anchorDate reset from `latest_sales_date`.
        // `data` is a dep because the scroller is only rendered once the
        // financial summary has loaded (component returns null before) —
        // the ref goes from null to a real element and we need to re-run.
        const raf = requestAnimationFrame(() => {
            const container = ref.current;
            if (!container) return;
            const el = container.querySelector(sel);
            if (!el) return;
            const contRect = container.getBoundingClientRect();
            const elRect = el.getBoundingClientRect();
            const target = container.scrollLeft
                + (elRect.left - contRect.left)
                - (container.clientWidth / 2)
                + (el.clientWidth / 2);
            container.scrollLeft = Math.max(0, target);
        });
        return () => cancelAnimationFrame(raf);
    }, [scope, activeKey, monthShortcuts, weekShortcuts, yearShortcuts, data]);

    // Aggregate daily series into months when viewing the year — fixed monthly grain
    // (per-day would be too dense to read). 12 points / year max.
    // When the N-1 overlay is requested, also aggregate the previous-year series
    // into the same monthly buckets under "prev_<Site>" keys.
    // In month scope: daily grain is kept; N-1 daily rows are re-keyed to the
    // current month's matching day so both series line up on the same X axis.
    const aggregatedSeries = useMemo(() => {
        if (!chartData?.series) return [];
        if (scope === 'week') return chartData.series;
        if (scope === 'month') {
            // Daily granularity. Start from current-year rows, then overlay N-1.
            const buckets = {}; // 'YYYY-MM-DD' -> { date, [site]: sum, prev_[site]: sum }
            for (const row of chartData.series) {
                if (!buckets[row.date]) buckets[row.date] = { date: row.date };
                for (const k of Object.keys(row)) {
                    if (k === 'date') continue;
                    if (row[k] === null || row[k] === undefined) continue;
                    buckets[row.date][k] = row[k];
                }
            }
            if (showPrevYear && prevYearData?.series) {
                for (const row of prevYearData.series) {
                    // Le jour N-1 (J-364) est re-calé sur le jour courant correspondant :
                    // même jour de semaine garanti (52 semaines pile).
                    const key = addDaysIso(row.date, 364);
                    if (!buckets[key]) buckets[key] = { date: key };
                    for (const k of Object.keys(row)) {
                        if (k === 'date') continue;
                        if (row[k] === null || row[k] === undefined) continue;
                        const prevKey = `prev_${k}`;
                        buckets[key][prevKey] = row[k];
                    }
                }
            }
            return Object.values(buckets).sort((a, b) => a.date.localeCompare(b.date));
        }
        // scope === 'year': monthly buckets
        const buckets = {}; // 'YYYY-MM' -> { date, [site]: sum }
        for (const row of chartData.series) {
            const ym = row.date.slice(0, 7);
            const anchor = `${ym}-15`;
            if (!buckets[ym]) buckets[ym] = { date: anchor };
            for (const k of Object.keys(row)) {
                if (k === 'date') continue;
                if (row[k] === null || row[k] === undefined) continue;
                buckets[ym][k] = Math.round(((buckets[ym][k] || 0) + row[k]) * 100) / 100;
            }
        }
        // Overlay N-1: re-key to the current year month and prefix with "prev_"
        if (showPrevYear && prevYearData?.series) {
            for (const row of prevYearData.series) {
                const mm = row.date.slice(5, 7);
                const currYm = `${anchorDate.slice(0, 4)}-${mm}`;
                const anchor = `${currYm}-15`;
                if (!buckets[currYm]) buckets[currYm] = { date: anchor };
                for (const k of Object.keys(row)) {
                    if (k === 'date') continue;
                    if (row[k] === null || row[k] === undefined) continue;
                    const prevKey = `prev_${k}`;
                    buckets[currYm][prevKey] = Math.round(((buckets[currYm][prevKey] || 0) + row[k]) * 100) / 100;
                }
            }
        }
        return Object.values(buckets).sort((a, b) => a.date.localeCompare(b.date));
    }, [chartData, scope, showPrevYear, prevYearData, anchorDate]);

    // Cumulative if requested
    const displaySeries = useMemo(() => {
        const src = aggregatedSeries;
        if (!src.length) return [];
        if (chartMode === 'daily') return src;
        const acc = {};
        return src.map(row => {
            const out = { date: row.date };
            for (const k of Object.keys(row)) {
                if (k === 'date') continue;
                if (row[k] === null || row[k] === undefined) {
                    // keep gap
                    out[k] = acc[k] !== undefined ? Math.round(acc[k] * 100) / 100 : null;
                } else {
                    acc[k] = (acc[k] || 0) + row[k];
                    out[k] = Math.round(acc[k] * 100) / 100;
                }
            }
            return out;
        });
    }, [aggregatedSeries, chartMode]);

    const sitesForLegend = (chartData?.sites?.map(s => s.name) || []).filter(n => !allowedNames || allowedNames.has(n));
    const toggleSite = (name) => setVisibleSites(v => ({ ...v, [name]: !v[name] }));

    // Restricted users: filter per-site rows and recompute totals on their sites only
    const filterBlock = (block) => {
        if (!allowedIds || !block) return block;
        const rows = block.rows.filter(r => allowedIds.includes(r.site_id));
        const ca = rows.reduce((s, r) => s + (r.ca || 0), 0);
        const caPrev = rows.reduce((s, r) => s + (r.ca_prev || 0), 0);
        return {
            ...block,
            rows,
            totals: {
                ca: Math.round(ca * 100) / 100,
                ca_prev: Math.round(caPrev * 100) / 100,
                delta_ca_pct: caPrev ? Math.round(((ca - caPrev) / caPrev) * 1000) / 10 : null,
                delta_ca_abs: Math.round((ca - caPrev) * 100) / 100,
            },
        };
    };

    if (loading && !data) {
        return <div className="py-12 text-center text-slate-400"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></div>;
    }
    if (!data) return null;

    return (
        <div className="space-y-3" data-testid="financial-tab">
            <div className="flex items-center gap-2">
                <Wallet className="w-5 h-5 text-emerald-500" />
                <h2 className="text-base font-semibold text-slate-800">Aperçu financier</h2>
                <span className="text-xs text-slate-500">CA réalisé par site, comparé à la période précédente</span>
                <Button size="sm" variant="outline" className="ml-auto h-7 text-xs" data-testid="missing-data-export-btn"
                    title="Exporter la liste des journées sans données de vente (5 sites Fuji Express)"
                    onClick={async () => {
                        try {
                            const res = await salesAPI.missingDataExport();
                            const url = URL.createObjectURL(new Blob([res.data]));
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = 'donnees_manquantes_fuji_express.xlsx';
                            a.click();
                            URL.revokeObjectURL(url);
                        } catch { /* toast? silent */ }
                    }}>
                    <FileDown className="w-3.5 h-3.5 mr-1" />Données manquantes
                </Button>
                <Button size="sm" variant="ghost" className="h-7" onClick={() => { reload(); reloadChart(); }} disabled={loading} data-testid="financial-refresh">
                    {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                </Button>
            </div>

            {/* Chart */}
            <Card className={scope === 'week' ? 'lg:w-1/2' : ''}>
                <CardHeader className="py-2 px-3 border-b space-y-2">
                    {/* Row 1: title + scope toggle + mode toggle + site toggles */}
                    <div className="flex flex-wrap items-center gap-2">
                        <LineChartIcon className="w-4 h-4 text-emerald-500" />
                        <CardTitle className="text-sm">Évolution du CA</CardTitle>
                        <Badge variant="outline" className="text-[10px] font-mono text-slate-500">{label}</Badge>
                        <div className="inline-flex bg-slate-200 rounded-md p-0.5 text-[11px] gap-0.5">
                            <button type="button" onClick={() => setScope('year')}
                                className={`px-2.5 py-0.5 rounded font-semibold transition-all ${scope === 'year' ? 'bg-slate-900 text-white shadow' : 'text-slate-600 hover:text-slate-900'}`}
                                data-testid="scope-year"
                            >Année</button>
                            <button type="button" onClick={() => setScope('month')}
                                className={`px-2.5 py-0.5 rounded font-semibold transition-all ${scope === 'month' ? 'bg-slate-900 text-white shadow' : 'text-slate-600 hover:text-slate-900'}`}
                                data-testid="scope-month"
                            >Mois</button>
                            <button type="button" onClick={() => setScope('week')}
                                className={`px-2.5 py-0.5 rounded font-semibold transition-all ${scope === 'week' ? 'bg-slate-900 text-white shadow' : 'text-slate-600 hover:text-slate-900'}`}
                                data-testid="scope-week"
                            >Semaine</button>
                        </div>
                        {(scope === 'year' || scope === 'month') && (
                            <label
                                className={`inline-flex items-center gap-1 px-2 h-6 rounded-md border cursor-pointer text-[11px] font-semibold transition-colors ${showPrevYear ? 'bg-amber-50 border-amber-300 text-amber-700' : 'bg-white border-slate-200 text-slate-500 hover:border-slate-400'}`}
                                data-testid="toggle-prev-year"
                                title={scope === 'year' ? 'Superposer l\u2019année N-1 en pointillé' : 'Superposer le même mois N-1 en pointillé, décalé pour aligner les jours de semaine'}
                            >
                                <Checkbox
                                    checked={showPrevYear}
                                    onCheckedChange={() => setShowPrevYear(v => !v)}
                                    className="w-3 h-3"
                                />
                                Vue N-1
                            </label>
                        )}
                        {scope === 'month' && showPrevYear && (() => {
                            const first = `${anchorDate.slice(0, 7)}-01`;
                            const prevFirst = addDaysIso(first, -364);
                            const calFirst = `${parseInt(anchorDate.slice(0, 4), 10) - 1}${first.slice(4)}`;
                            const diff = Math.round((new Date(prevFirst + 'T12:00:00') - new Date(calFirst + 'T12:00:00')) / 86400000);
                            const fr = (iso) => iso.split('-').reverse().join('/');
                            return (
                                <span className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5" data-testid="n1-shift-note">
                                    N-1 aligné jours de semaine : {fr(first)} ↔ {fr(prevFirst)} (décalage {diff >= 0 ? '+' : ''}{diff} j)
                                </span>
                            );
                        })()}
                        <div className="ml-auto flex items-center gap-2">
                            <div className="inline-flex bg-slate-200 rounded-md p-0.5 text-[11px] gap-0.5">
                                <button type="button" onClick={() => setChartMode('daily')}
                                    className={`px-2.5 py-0.5 rounded font-semibold transition-all ${chartMode === 'daily' ? 'bg-slate-900 text-white shadow' : 'text-slate-600 hover:text-slate-900'}`}
                                    data-testid="chart-mode-daily"
                                >Quotidien</button>
                                <button type="button" onClick={() => setChartMode('cumul')}
                                    className={`px-2.5 py-0.5 rounded font-semibold transition-all ${chartMode === 'cumul' ? 'bg-slate-900 text-white shadow' : 'text-slate-600 hover:text-slate-900'}`}
                                    data-testid="chart-mode-cumul"
                                >Cumulé</button>
                            </div>
                            <div className="flex flex-wrap items-center gap-1.5" data-testid="chart-site-toggles">
                                {sitesForLegend.map(name => {
                                    const checked = !!visibleSites[name];
                                    const color = SITE_COLORS[name] || '#64748b';
                                    return (
                                        <label key={name}
                                            className={`inline-flex items-center gap-1 px-2 h-6 rounded-full border cursor-pointer transition-colors text-[11px] font-semibold ${checked ? 'bg-white border-slate-300' : 'bg-slate-50 border-slate-200 text-slate-400'}`}
                                            style={{ borderColor: checked ? color : undefined, color: checked ? color : undefined }}
                                        >
                                            <Checkbox checked={checked} onCheckedChange={() => toggleSite(name)} className="w-3 h-3" data-testid={`chart-toggle-${name.replace('FE ', '').toLowerCase()}`} />
                                            {name.replace('FE ', '')}
                                        </label>
                                    );
                                })}
                                {!allowedIds && (
                                <label className={`inline-flex items-center gap-1 px-2 h-6 rounded-full border cursor-pointer text-[11px] font-semibold ${visibleSites.Total ? 'bg-slate-900 text-white border-slate-900' : 'bg-slate-50 border-slate-200 text-slate-400'}`}>
                                    <Checkbox checked={!!visibleSites.Total} onCheckedChange={() => toggleSite('Total')} className="w-3 h-3" data-testid="chart-toggle-total" />
                                    Total
                                </label>
                                )}
                            </div>
                        </div>
                    </div>
                    {/* Row 2: shortcuts */}
                    {scope === 'year' ? (
                        <div className="flex items-center gap-1">
                            <button
                                type="button"
                                onClick={() => { if (yearScrollerRef.current) yearScrollerRef.current.scrollLeft -= 160; }}
                                className="h-6 w-5 flex-shrink-0 text-slate-400 hover:text-slate-700 text-sm"
                                title="Reculer"
                            >‹</button>
                            <div ref={yearScrollerRef} className="flex-1 overflow-x-auto" style={{ scrollBehavior: 'smooth' }}>
                                <div className="flex gap-1 min-w-max pb-0.5">
                                    {yearShortcuts.map(s => {
                                        const active = s.key === activeKey;
                                        return (
                                            <button key={s.key} type="button"
                                                onClick={() => setAnchorDate(s.anchor)}
                                                data-year-key={s.key}
                                                className={`shrink-0 px-3 h-6 rounded-md border text-[11px] font-semibold transition-colors ${active ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}
                                                data-testid={`shortcut-year-${s.key}`}
                                            >{s.label}</button>
                                        );
                                    })}
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => { if (yearScrollerRef.current) yearScrollerRef.current.scrollLeft += 160; }}
                                className="h-6 w-5 flex-shrink-0 text-slate-400 hover:text-slate-700 text-sm"
                                title="Avancer"
                            >›</button>
                        </div>
                    ) : scope === 'month' ? (
                        <div className="flex items-center gap-1">
                            <button
                                type="button"
                                onClick={() => { if (monthScrollerRef.current) monthScrollerRef.current.scrollLeft -= 200; }}
                                className="h-6 w-5 flex-shrink-0 text-slate-400 hover:text-slate-700 text-sm"
                                title="Reculer"
                            >‹</button>
                            <div ref={monthScrollerRef} className="flex-1 overflow-x-auto" style={{ scrollBehavior: 'smooth' }}>
                                <div className="flex gap-1 min-w-max pb-0.5">
                                    {monthShortcuts.map(s => {
                                        const active = s.key === activeKey;
                                        return (
                                            <button key={s.key} type="button"
                                                onClick={() => setAnchorDate(s.anchor)}
                                                data-month-key={s.key}
                                                className={`shrink-0 px-2 h-6 rounded-md border text-[11px] font-semibold transition-colors ${active ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}
                                                data-testid={`shortcut-${s.key}`}
                                            >{s.label}</button>
                                        );
                                    })}
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => { if (monthScrollerRef.current) monthScrollerRef.current.scrollLeft += 200; }}
                                className="h-6 w-5 flex-shrink-0 text-slate-400 hover:text-slate-700 text-sm"
                                title="Avancer"
                            >›</button>
                        </div>
                    ) : (
                        <div className="flex items-center gap-1">
                            <button
                                type="button"
                                onClick={() => { if (weekScrollerRef.current) weekScrollerRef.current.scrollLeft -= 220; }}
                                className="h-6 w-5 flex-shrink-0 text-slate-400 hover:text-slate-700 text-sm"
                                title="Reculer"
                            >‹</button>
                            <div ref={weekScrollerRef} className="flex-1 overflow-x-auto" style={{ scrollBehavior: 'smooth' }}>
                                <div className="flex gap-1 min-w-max pb-0.5">
                                    {weekShortcuts.map(s => {
                                        const active = s.key === activeKey;
                                        return (
                                            <button key={s.key} type="button"
                                                onClick={() => setAnchorDate(s.anchor)}
                                                data-week-key={s.key}
                                                className={`shrink-0 px-2 h-6 rounded-md border text-[11px] font-semibold transition-colors ${active ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}
                                                data-testid={`shortcut-${s.key}`}
                                            >{s.label}</button>
                                        );
                                    })}
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => { if (weekScrollerRef.current) weekScrollerRef.current.scrollLeft += 220; }}
                                className="h-6 w-5 flex-shrink-0 text-slate-400 hover:text-slate-700 text-sm"
                                title="Avancer"
                            >›</button>
                        </div>
                    )}
                </CardHeader>
                <CardContent className="p-2">
                    {chartLoading ? (
                        <div className="h-72 flex items-center justify-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
                    ) : (
                        <ResponsiveContainer width="100%" height={320}>
                            <LineChart data={displaySeries} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                {scope !== 'year' && holidayAreas(displaySeries.map(r => r.date)).map((a, i) => (
                                    <ReferenceArea key={i} x1={a.x1} x2={a.x2} fill="#38bdf8" fillOpacity={0.07}
                                        label={{ value: `Vac. ${a.label}`, position: 'insideTopLeft', fontSize: 9, fill: '#0284c7', opacity: 0.8 }} />
                                ))}
                                <XAxis
                                    dataKey="date"
                                    tickFormatter={(v) => {
                                        if (scope === 'year') {
                                            const m = parseInt(v.slice(5, 7), 10);
                                            return MONTHS_FR[m - 1] || v;
                                        }
                                        return `${v.slice(8, 10)}/${v.slice(5, 7)}`;
                                    }}
                                    tick={(props) => {
                                        const { x, y, payload } = props;
                                        const hasNote = !!notesByDate[payload.value];
                                        const isYear = scope === 'year';
                                        const labelText = isYear
                                            ? (MONTHS_FR[parseInt(payload.value.slice(5, 7), 10) - 1] || payload.value)
                                            : `${payload.value.slice(8, 10)}/${payload.value.slice(5, 7)}`;
                                        // First letter of weekday (D/L/M/M/J/V/S), shown below the date in week/month views.
                                        // Mirrors the daily pivot in the Sales > Ventes tab.
                                        // Weekend (Sun=0, Sat=6) is colored red to spot them quickly.
                                        let dowLetter = '';
                                        let dowFill = '#94a3b8'; // slate-400
                                        let dowWeight = 'normal';
                                        if (!isYear) {
                                            const d = new Date(payload.value + 'T00:00:00');
                                            const dayIdx = d.getDay();
                                            dowLetter = ['D', 'L', 'M', 'M', 'J', 'V', 'S'][dayIdx];
                                            if (dayIdx === 0 || dayIdx === 6) {
                                                dowFill = '#f87171'; // red-400
                                                dowWeight = 'bold';
                                            }
                                        }
                                        return (
                                            <g transform={`translate(${x},${y})`}>
                                                <text x={0} y={0} dy={12} textAnchor="middle" fontSize={11} fill="#64748b">
                                                    {labelText}
                                                </text>
                                                {!isYear && (
                                                    <text x={0} y={0} dy={22} textAnchor="middle" fontSize={9} fill={dowFill} fontWeight={dowWeight}>
                                                        {dowLetter}
                                                    </text>
                                                )}
                                                {hasNote && !isYear && <circle cx={0} cy={30} r={2.5} fill="#f59e0b" />}
                                            </g>
                                        );
                                    }}
                                    interval={scope === 'week' || scope === 'year' ? 0 : 'preserveStartEnd'}
                                    height={scope === 'year' ? 30 : 36}
                                />
                                <YAxis tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} tick={{ fontSize: 11, fill: '#64748b' }} width={50} />
                                <Tooltip
                                    contentStyle={{ fontSize: 11, borderRadius: 6 }}
                                    content={({ active, payload, label }) => {
                                        if (!active || !payload?.length) return null;
                                        const dayNotes = notesByDate[label] || [];
                                        const d = new Date(label + 'T00:00:00');
                                        const dayIdx = d.getDay();
                                        const dow = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'][dayIdx];
                                        const isWeekend = dayIdx === 0 || dayIdx === 6;
                                        return (
                                            <div className="bg-white border rounded-md shadow-lg p-2 text-[11px] max-w-[280px]">
                                                <div className={`font-semibold mb-1 ${isWeekend ? 'text-red-500' : 'text-slate-700'}`}>{dow} {label}</div>
                                                {payload.filter(p => p.value !== null && p.value !== undefined).map(p => {
                                                    const isPrev = p.dataKey.startsWith('prev_');
                                                    const cleanName = (isPrev ? p.dataKey.slice(5) : p.dataKey).replace('FE ', '');
                                                    return (
                                                        <div key={p.dataKey} className="flex items-center justify-between gap-2">
                                                            <span style={{ color: p.color, opacity: isPrev ? 0.6 : 1 }}>
                                                                {isPrev ? '◌' : '●'} {cleanName}{isPrev ? ' (N-1)' : ''}
                                                            </span>
                                                            <span className="tabular-nums font-semibold">{formatCurrency(p.value)}</span>
                                                        </div>
                                                    );
                                                })}
                                                {dayNotes.length > 0 && (
                                                    <div className="mt-1.5 pt-1.5 border-t space-y-1">
                                                        {dayNotes.map(n => {
                                                            const cat = getCategoryStyle(n.category);
                                                            return (
                                                                <div key={n.id} className="text-[10px]">
                                                                    <span className={`inline-block px-1 py-0 rounded text-[9px] font-semibold mr-1 ${cat.color}`}>{cat.label}</span>
                                                                    {n.site_name && <span className="text-slate-500">[{n.site_name.replace('FE ', '')}] </span>}
                                                                    <span className="text-slate-700">{n.text}</span>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    }}
                                />
                                <Legend wrapperStyle={{ fontSize: 11 }} formatter={(v) => v.replace('FE ', '').replace('prev_', '').replace(/^(\S+)/, '$1')} />
                                {sitesForLegend.map(name => visibleSites[name] && (
                                    <Line
                                        key={name}
                                        type="monotone"
                                        dataKey={name}
                                        stroke={SITE_COLORS[name] || '#64748b'}
                                        strokeWidth={2}
                                        dot={{ r: 2 }}
                                        activeDot={{ r: 4 }}
                                        isAnimationActive={false}
                                        connectNulls={false}
                                    />
                                ))}
                                {(scope === 'year' || scope === 'month') && showPrevYear && sitesForLegend.map(name => visibleSites[name] && (
                                    <Line
                                        key={`prev_${name}`}
                                        type="monotone"
                                        dataKey={`prev_${name}`}
                                        name={`${name.replace('FE ', '')} (N-1)`}
                                        stroke={SITE_COLORS[name] || '#64748b'}
                                        strokeWidth={1.5}
                                        strokeDasharray="3 3"
                                        strokeOpacity={0.6}
                                        dot={false}
                                        activeDot={{ r: 3 }}
                                        isAnimationActive={false}
                                        connectNulls={false}
                                    />
                                ))}
                                {!allowedIds && visibleSites.Total && (
                                    <Line
                                        type="monotone"
                                        dataKey="Total"
                                        stroke={SITE_COLORS.Total}
                                        strokeWidth={2.5}
                                        strokeDasharray="4 4"
                                        dot={{ r: 2 }}
                                        activeDot={{ r: 4 }}
                                        isAnimationActive={false}
                                        connectNulls={false}
                                    />
                                )}
                                {(scope === 'year' || scope === 'month') && showPrevYear && !allowedIds && visibleSites.Total && (
                                    <Line
                                        type="monotone"
                                        dataKey="prev_Total"
                                        name="Total (N-1)"
                                        stroke={SITE_COLORS.Total}
                                        strokeWidth={1.5}
                                        strokeDasharray="2 4"
                                        strokeOpacity={0.5}
                                        dot={false}
                                        activeDot={{ r: 3 }}
                                        isAnimationActive={false}
                                        connectNulls={false}
                                    />
                                )}
                            </LineChart>
                        </ResponsiveContainer>
                    )}
                </CardContent>
            </Card>

            {/* Notes list for this period (placed right below the chart).
                In "Année" view we collapse by default to keep the page compact;
                user can expand on demand. */}
            <NotesPanel
                notes={visibleNotes}
                sites={feSites}
                startDate={startDate}
                endDate={endDate}
                onChanged={reloadNotesOnly}
                collapsible={scope === 'year'}
                open={scope === 'year' ? notesPanelOpen : true}
                onOpenChange={setNotesPanelOpen}
            />

            {/* Prédictif CA 30 j + Semaine personnel (permission cochable) */}
            {hasPermission('sales_forecast_view') && (
                <>
                    <ForecastSection />
                    <StaffingWeek />
                </>
            )}

            {/* Period summary cards (Quotidien / Hebdomadaire / Mensuel) */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                {PERIODS.map(p => (
                    <PeriodCard key={p.id} period={p} block={filterBlock(data.periods[p.id])} />
                ))}
            </div>

            <div className="text-[11px] text-slate-400 italic pt-1">
                Données mises à jour 3×/jour via la synchronisation Gmail automatique. Le « Quotidien » affiche le dernier jour
                clos (J-1) car les ventes du jour ne sont consolidées que le lendemain matin. Les jours sans vente apparaissent
                comme une rupture dans la courbe.
            </div>
        </div>
    );
}

// --------- Notes panel ---------
function NotesPanel({ notes, sites, startDate, endDate, onChanged, collapsible = false, open = true, onOpenChange }) {
    const sortedNotes = [...notes].sort((a, b) => a.date.localeCompare(b.date));
    return (
        <Card>
            <CardHeader className="py-2 px-3 border-b">
                <div className="flex items-center gap-2">
                    <CardTitle className="text-sm">Notes contextuelles</CardTitle>
                    <Badge variant="outline" className="text-[10px]">{sortedNotes.length}</Badge>
                    <span className="text-[11px] text-slate-400 ml-1 hidden sm:inline">Justifications, fermetures, événements…</span>
                    <div className="ml-auto flex items-center gap-1.5">
                        {collapsible && (
                            <button
                                type="button"
                                onClick={() => onOpenChange && onOpenChange(!open)}
                                className="text-[11px] font-semibold text-slate-500 hover:text-slate-800 border border-slate-200 rounded h-7 px-2 flex items-center gap-1"
                                data-testid="toggle-notes-panel-btn"
                                title={open ? 'Masquer le tableau des notes' : 'Afficher le tableau des notes'}
                            >
                                {open ? '▼' : '▶'} {open ? 'Masquer' : 'Afficher'}
                            </button>
                        )}
                        <SalesNotePopover
                            dateIso={endDate}
                            sites={sites}
                            notes={[]}
                            onChanged={onChanged}
                            variant="button"
                            label="Nouvelle note"
                        />
                    </div>
                </div>
            </CardHeader>
            {open && (
            <CardContent className="p-0">
                {sortedNotes.length === 0 ? (
                    <div className="px-3 py-4 text-center text-xs text-slate-400 italic">
                        Aucune note sur la période ({startDate} → {endDate}). Cliquez sur l'icône <span className="inline-flex w-4 h-4 align-middle bg-amber-100 rounded-full"></span> sur un jour pour en ajouter.
                    </div>
                ) : (
                    <table className="w-full text-xs">
                        <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
                            <tr>
                                <th className="text-left px-3 py-1 w-24">Date</th>
                                <th className="text-left px-3 py-1 w-24">Catégorie</th>
                                <th className="text-left px-3 py-1 w-24">Site</th>
                                <th className="text-left px-3 py-1">Note</th>
                                <th className="w-10"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedNotes.map(n => {
                                const cat = getCategoryStyle(n.category);
                                const d = new Date(n.date + 'T00:00:00');
                                const dayIdx = d.getDay();
                                const dow = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'][dayIdx];
                                const isWeekend = dayIdx === 0 || dayIdx === 6;
                                return (
                                    <tr key={n.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                                        <td className={`px-3 py-1 font-mono whitespace-nowrap ${isWeekend ? 'text-red-500 font-semibold' : 'text-slate-600'}`}>{dow} {n.date}</td>
                                        <td className="px-3 py-1">
                                            <Badge className={`text-[10px] ${cat.color}`}>{cat.label}</Badge>
                                        </td>
                                        <td className="px-3 py-1 text-slate-600">
                                            {n.site_name ? n.site_name.replace('FE ', '') : <span className="text-slate-400 italic">Tous</span>}
                                        </td>
                                        <td className="px-3 py-1 text-slate-700">{n.text}</td>
                                        <td className="px-3 py-1">
                                            <SalesNotePopover
                                                dateIso={n.date}
                                                siteId={n.site_id}
                                                siteName={n.site_name}
                                                sites={sites}
                                                notes={notes}
                                                onChanged={onChanged}
                                            />
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </CardContent>
            )}
        </Card>
    );
}

// --------- Helpers ---------

function todayIso() { return new Date().toISOString().slice(0, 10); }
function toIso(d) { return d.toISOString().slice(0, 10); }
function isoWeekNumber(date) {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
}

function buildMonthShortcuts() {
    // From November 2020 (début historique) to current month
    const today = new Date();
    const arr = [];
    let lastYear = null;
    const startYear = 2020, startMonth = 11;
    let y = startYear, m = startMonth;
    while (y < today.getFullYear() || (y === today.getFullYear() && m <= today.getMonth() + 1)) {
        const key = `${y}-${String(m).padStart(2, '0')}`;
        const showYear = (m === 1) || lastYear === null || lastYear !== y;
        lastYear = y;
        arr.push({
            key,
            anchor: `${key}-15`,
            label: showYear ? `${MONTHS_FR[m - 1]} ${String(y).slice(2)}` : `${MONTHS_FR[m - 1]}`,
        });
        m += 1;
        if (m > 12) { m = 1; y += 1; }
    }
    return arr;
}

function buildYearShortcuts() {
    // From 2020 (début historique) to current year
    const today = new Date();
    const arr = [];
    for (let y = 2020; y <= today.getFullYear(); y++) {
        arr.push({ key: String(y), anchor: `${y}-06-15`, label: String(y) });
    }
    return arr;
}


function buildWeekShortcuts() {
    // Every Monday from week containing 2020-11-01 (début historique) to current week
    const today = new Date();
    const dow = today.getDay();
    const diff = dow === 0 ? -6 : 1 - dow;
    const currentMonday = new Date(today); currentMonday.setDate(today.getDate() + diff);
    // Start: Monday of week containing 2020-11-01
    let start = new Date(2020, 10, 1);
    const sdow = start.getDay();
    const sdiff = sdow === 0 ? -6 : 1 - sdow;
    start.setDate(start.getDate() + sdiff);
    const arr = [];
    let lastMonthLabel = null;
    let cur = new Date(start);
    while (cur <= currentMonday) {
        const w = isoWeekNumber(cur);
        const monthLabel = `${MONTHS_FR[cur.getMonth()]} ${String(cur.getFullYear()).slice(2)}`;
        const showMonth = monthLabel !== lastMonthLabel;
        lastMonthLabel = monthLabel;
        arr.push({
            key: toIso(cur),
            anchor: toIso(cur),
            label: showMonth ? `S${w} · ${monthLabel}` : `S${w}`,
        });
        cur.setDate(cur.getDate() + 7);
    }
    return arr;
}

function PeriodCard({ period, block }) {
    if (!block) return null;
    const { label_current, label_previous, rows, totals } = block;

    return (
        <Card className="overflow-hidden" data-testid={`period-card-${period.id}`}>
            <CardHeader className="py-2 px-3 bg-gradient-to-r from-slate-50 to-white border-b">
                <div className="flex items-baseline justify-between">
                    <div>
                        <CardTitle className="text-sm font-semibold text-slate-800">{period.label}</CardTitle>
                        <div className="text-[10px] text-slate-400 mt-0.5">{period.subtitle}</div>
                    </div>
                    <Badge variant="outline" className="text-[10px] font-mono text-slate-500 border-slate-200">
                        {label_current}
                    </Badge>
                </div>
            </CardHeader>
            <CardContent className="p-0">
                {/* Total */}
                <div className="px-3 py-2 bg-slate-900 text-white">
                    <div className="flex items-baseline justify-between">
                        <span className="text-[11px] uppercase tracking-wider text-slate-400">
                            {rows.length === 1 ? rows[0].site_name : `Total ${rows.length} sites`}
                        </span>
                        <DeltaBadge value={totals.delta_ca_pct} inverted />
                    </div>
                    <div className="flex items-baseline gap-2 mt-0.5">
                        <span className="text-2xl font-bold tabular-nums">{formatCurrency(totals.ca)}</span>
                        <span className="text-[11px] text-slate-400 tabular-nums">vs {formatCurrency(totals.ca_prev)}</span>
                    </div>
                </div>
                {/* Per site */}
                <table className="w-full text-[11px]">
                    <thead className="bg-slate-50 border-b text-[10px] uppercase text-slate-500">
                        <tr>
                            <th className="text-left px-3 py-1">Site</th>
                            <th className="text-right px-3 py-1">CA</th>
                            <th className="text-right px-3 py-1">Δ</th>
                            <th className="text-right px-3 py-1 hidden sm:table-cell">Qté</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(r => (
                            <tr key={r.site_id} className="border-b border-slate-100 hover:bg-slate-50/60">
                                <td className="px-3 py-1 font-medium text-slate-700">
                                    {r.site_name?.replace('FE ', '')}
                                </td>
                                <td className="px-3 py-1 text-right tabular-nums font-semibold text-slate-800">
                                    {r.ca > 0 ? formatCurrency(r.ca) : <span className="text-slate-300">—</span>}
                                </td>
                                <td className="px-3 py-1 text-right">
                                    <DeltaBadge value={r.delta_ca_pct} compact />
                                </td>
                                <td className="px-3 py-1 text-right tabular-nums text-slate-500 hidden sm:table-cell">
                                    {r.qty > 0 ? r.qty.toFixed(0) : <span className="text-slate-300">—</span>}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {/* Footer details */}
                <div className="px-3 py-1.5 bg-slate-50 text-[10px] text-slate-500 flex justify-between border-t">
                    <span>Période N-1 : {label_previous}</span>
                    <span className="tabular-nums">
                        {totals.delta_ca_abs > 0 ? '+' : ''}{formatCurrency(totals.delta_ca_abs)}
                    </span>
                </div>
            </CardContent>
        </Card>
    );
}

function DeltaBadge({ value, compact = false, inverted = false }) {
    if (value === null || value === undefined) {
        return <span className={`text-[10px] ${inverted ? 'text-slate-400' : 'text-slate-300'}`}>—</span>;
    }
    const positive = value > 0;
    const neutral = Math.abs(value) < 0.5;
    let cls;
    if (neutral) {
        cls = inverted ? 'text-slate-300' : 'bg-slate-100 text-slate-500';
    } else if (positive) {
        cls = inverted ? 'text-emerald-300' : 'bg-emerald-100 text-emerald-700';
    } else {
        cls = inverted ? 'text-red-300' : 'bg-red-100 text-red-700';
    }
    const Icon = neutral ? null : positive ? TrendingUp : TrendingDown;
    return (
        <span
            className={`inline-flex items-center gap-0.5 ${inverted ? '' : 'px-1.5 py-0.5 rounded-full'} text-[10px] font-semibold tabular-nums ${cls}`}
        >
            {Icon && <Icon className="w-2.5 h-2.5" />}
            {positive ? '+' : ''}{value.toFixed(1)}%
        </span>
    );
}
